import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { LocaleLink } from "@/components/LocaleLink";
import { AuroraBackground } from "@/components/AuroraBackground";
import { Reveal } from "@/components/Reveal";
import { StoreBadgeGroup } from "@/components/StoreBadges";
import { BookOpen, ChevronRight } from "lucide-react";

const GUIDES = [
  { slug: "relationship-green-flags", tag: "Connection" },
  { slug: "relationship-red-flags-vs-rough-patches", tag: "Decision" },
  { slug: "how-to-set-boundaries-in-your-relationship", tag: "Connection" },
  { slug: "in-laws-and-boundaries-for-couples", tag: "Conflict" },
  { slug: "surviving-infidelity-first-30-days", tag: "Trust" },
  { slug: "relationship-anxiety-right-person", tag: "Connection" },
  { slug: "parenting-disagreements-united-team", tag: "Seasons" },
  { slug: "work-stress-and-your-relationship", tag: "Connection" },
  { slug: "gratitude-practice-for-couples", tag: "Rituals" },
  { slug: "how-to-forgive-your-partner", tag: "Repair" },
  { slug: "sleeping-apart-sleep-divorce", tag: "Intimacy" },
  { slug: "mental-load-invisible-labor-relationship", tag: "Conflict" },
  { slug: "rekindle-attraction-long-term-relationship", tag: "Intimacy" },
  { slug: "intimacy-drought-how-to-reconnect", tag: "Intimacy" },
  { slug: "how-to-fight-fair-rules-for-couples", tag: "Conflict" },
  { slug: "anxious-avoidant-relationship-attachment", tag: "Connection" },
  { slug: "supporting-partner-with-anxiety", tag: "Connection" },
  { slug: "wedding-planning-stress-relationship", tag: "Seasons" },
  { slug: "blended-family-relationship-challenges", tag: "Seasons" },
  { slug: "retiring-together-marriage-adjustment", tag: "Seasons" },
  { slug: "how-to-rebuild-trust-in-a-relationship", tag: "Trust" },
  { slug: "how-to-communicate-better-with-your-partner", tag: "Connection" },
  { slug: "feeling-lonely-in-a-relationship", tag: "Repair" },
  { slug: "how-to-stop-fighting-about-money", tag: "Conflict" },
  { slug: "jealousy-in-relationships", tag: "Repair" },
  { slug: "moving-in-together-checklist", tag: "Seasons" },
  { slug: "empty-nest-marriage", tag: "Seasons" },
  { slug: "date-night-ideas-at-home", tag: "Rituals" },
  { slug: "how-to-apologize-to-your-partner", tag: "Conflict" },
  { slug: "stonewalling-in-relationships", tag: "Conflict" },
  { slug: "stop-having-the-same-fight", tag: "Conflict" },
  { slug: "roommate-syndrome-couples", tag: "Repair" },
  { slug: "emotional-intimacy-exercises", tag: "Connection" },
  { slug: "deep-questions-for-couples", tag: "Connection" },
  { slug: "love-languages-daily-practice", tag: "Connection" },
  { slug: "signs-your-relationship-is-worth-saving", tag: "Decision" },
  { slug: "what-to-say-after-a-fight", tag: "Conflict" },
  { slug: "signs-of-drifting-apart", tag: "Repair" },
  { slug: "marriage-check-in-questions", tag: "Connection" },
  { slug: "daily-intimacy-rituals", tag: "Connection" },
  { slug: "reconnecting-emotionally", tag: "Repair" },
  { slug: "resolving-shutdown-conflict", tag: "Conflict" },
  { slug: "navigating-mismatched-desire", tag: "Intimacy" },
  { slug: "supporting-partner-cycle", tag: "Tracking" },
  { slug: "long-distance-intimacy", tag: "Connection" },
  { slug: "staying-connected-new-parents", tag: "Seasons" },
  { slug: "celebrating-milestones", tag: "Rituals" },
  { slug: "keeping-playful-long-term", tag: "Connection" },
  { slug: "faith-aware-relationship-guidance", tag: "Faith" },
  { slug: "is-it-worth-trying-counseling-before-divorce", tag: "Decision" },
  { slug: "privacy-and-security", tag: "Trust" },
];

const KnowledgePage = () => {
  const { t } = useTranslation("site");
  const tk = (k, fb) => t(`knowledge.${k}`, fb);
  return (
    <>
      <section className="relative pb-12">
        <AuroraBackground variant="soft" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 pt-8 lg:pt-12">
          <Reveal>
            <p className="eyebrow">{tk("kicker")}</p>
            <h1 className="mt-3 font-serif text-[2.4rem] sm:text-5xl lg:text-[4rem] tracking-tight font-light leading-[1.05] max-w-4xl">
              {tk("title1")}{" "}<em className="italic aurora-shimmer-text">{tk("title2")}</em>{" "}{tk("title3")}
            </h1>
            <p className="mt-6 max-w-2xl text-white/72 text-lg font-light">{tk("subtitle")}</p>
          </Reveal>
        </div>
      </section>

      <section className="relative py-16">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {GUIDES.map((g, i) => (
            <Reveal key={g.slug} delay={(i % 6) * 50}>
              <LocaleLink
                to={`/knowledge/${g.slug}`}
                className="aurora-card p-6 h-full block group"
                style={{ background: ["var(--aurora-coach)", "var(--aurora-journeys)", "var(--aurora-emotional)"][i % 3] }}
                data-testid={`guide-${g.slug}`}
              >
                <div className="flex items-center gap-2 mb-4">
                  <BookOpen size={16} className="text-[var(--aurora-gold)]" />
                  <span className="text-[0.68rem] uppercase tracking-[0.22em] text-white/55">
                    {tk(`tags.${g.tag}`)}
                  </span>
                </div>
                <h3 className="font-serif text-xl leading-snug">{tk(`guides.${g.slug}`)}</h3>
                <span className="mt-5 inline-flex items-center gap-1 text-[var(--aurora-cyan)] text-sm group-hover:gap-2 transition-all">
                  {t("common.readMore")} <ChevronRight size={14} />
                </span>
              </LocaleLink>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="relative py-20">
        <div className="mx-auto max-w-3xl px-5 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight font-light">
              {tk("closingTitle1")}{" "}<em className="italic text-[var(--aurora-gold)]">{tk("closingTitle2")}</em>
            </h2>
            <p className="mt-4 text-white/72 font-light">{tk("closingBody")}</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <StoreBadgeGroup startLabel={t("common.startFree")} />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default KnowledgePage;
